import { connectionsSlice } from '@databases/connections-api/slice';
import { getCloudId } from '@databases/database-state';
import { defaultStoreUtils } from '@databases/redux';
import { type DatabaseField, DatabaseFieldType } from '@databases/types/database';
import { requiredJiraColumns } from '@databases/utils/confluence/jira';
import { getDatabaseFields, isNonBrokenDatabase } from '@databases/utils/database/utils';
import { selectAllDatabases } from '@databases/utils/redux/databases/selectors';
import { useEffect } from 'react';
import { useIntl } from 'react-intl-next';

import { requiredConfluenceDatasourceColumns } from '../content/datasource';
import { messages } from '../i18n';

export const useConnections = () => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();

  const fields = defaultStoreUtils.useEqualSelector((state) => {
    const databases = selectAllDatabases(state);

    return databases
      .map((database) => {
        return database && isNonBrokenDatabase(database)
          ? getDatabaseFields(database)
          : undefined;
      })
      .flat()
      .filter((item) => item) as DatabaseField[];
  });

  const connections = defaultStoreUtils.useEqualSelector(
    (state) => state.connections.connections
  );

  useEffect(() => {
    const processConnections = () => {
      if (!fields) {
        return;
      }

      const connectionIdsWithType = Object.entries(connections).map(
        ([connectionId, connection]) => ({
          connectionId,
          type: connection.type
        })
      );

      // we don't want to re-process the same fields
      const newConnectionFields = fields.filter(
        (field) =>
          [DatabaseFieldType.PAGE_LINK, DatabaseFieldType.JIRA_ISSUE].includes(
            field.type
          ) &&
          !connectionIdsWithType.find((connection) => {
            const connectionType =
              field.type === DatabaseFieldType.PAGE_LINK ? 'confluence' : 'jira';
            return (
              connection.connectionId === field.id && connection.type === connectionType
            );
          })
      );

      if (newConnectionFields.length === 0) {
        return;
      }

      for (const field of fields) {
        if (
          [DatabaseFieldType.PAGE_LINK, DatabaseFieldType.JIRA_ISSUE].includes(field.type)
        ) {
          const type = field.type === DatabaseFieldType.PAGE_LINK ? 'confluence' : 'jira';
          dispatch(
            connectionsSlice.actions.addConnection({
              cloudId: field.settings.cloudId ?? getCloudId() ?? '',
              connectionId: field.id,
              type,
              requiredColumns:
                type === 'confluence'
                  ? [...requiredConfluenceDatasourceColumns]
                  : [...requiredJiraColumns]
            })
          );
        }
      }

      for (const field of fields) {
        if (
          [
            DatabaseFieldType.PAGE_DETAILS,
            DatabaseFieldType.PAGE_STATUS,
            DatabaseFieldType.PAGE_LABELS,
            DatabaseFieldType.JIRA_ISSUE_DETAILS
          ].includes(field.type)
        ) {
          const column =
            field.type === DatabaseFieldType.PAGE_LABELS
              ? 'labels'
              : (field.settings.contentField ?? field.settings.jiraField);
          if (field.settings.fieldId && column) {
            if (column === 'version' && field.settings.includeVersionComment) {
              dispatch(
                connectionsSlice.actions.addColumnToConnection({
                  connectionId: field.settings.fieldId,
                  column: {
                    key: 'versionComment',
                    type: 'string',
                    title: formatMessage(
                      messages['connections.field.title.version-comment']
                    )
                  }
                })
              );
            } else if (column === 'excerpt') {
              dispatch(
                connectionsSlice.actions.addColumnToConnection({
                  connectionId: field.settings.fieldId,
                  column: {
                    key: 'excerpt',
                    type: 'html',
                    title: formatMessage(messages['connections.field.title.excerpt'])
                  }
                })
              );
            } else {
              dispatch(
                connectionsSlice.actions.addRequiredColumnToConnection({
                  connectionId: field.settings.fieldId,
                  column
                })
              );
            }
          }

          if (field.settings.fieldId && field.type === DatabaseFieldType.PAGE_STATUS) {
            dispatch(
              connectionsSlice.actions.addColumnToConnection({
                connectionId: field.settings.fieldId,
                column: {
                  key: 'pageStatus',
                  type: 'pageStatus',
                  title: formatMessage(messages['connections.field.title.page_status'])
                }
              })
            );
          }
        }
      }
    };

    processConnections();
  }, [dispatch, fields, formatMessage, connections]);
};
